import { StyleVariant } from '../../../types'
import { ModelExtensionConfigBase } from '../../ModelExtensionModelsLoader/common'

/**
 * RecordComments の テーブル名の接尾辞
 */
export const recordCommentExtensionModelNameSuffix = '__extension__record_comments'

/**
 * データ側のModel定義に付与される、RecordComments を O2M Relationship で参照する際のカラム名
 */
export const recordCommentOneToManyColName = 'ext__record_comments'
/**
 * RecordComments の テーブル 内で、元レコードの ID もしくは primaryKey 値を参照するカラム名 (M2O Relationship)
 */
export const recordCommentManyToOneRelationshipColName = 'target_record'
export const getExtensionCommentsModelNameByBaseModelName = (
  baseModelName: string,
): string => {
  return `${baseModelName}${recordCommentExtensionModelNameSuffix}`
}

export const recordCommentDisplayStyles = {
  default: {},
}

/**
 * RecordComments の Model定義に付与する 設定の型
 * ModelDef.extensions.recordComments へ設定する
 */
export type ModelExtensionRecordCommentsConfig = ModelExtensionConfigBase & {
  extensionType: 'recordComments'
  /**
   * true を指定すると レコードコメント機能が有効になる。
   */
  enabled?: boolean
  /**
   * レコードコメント機能を有効にする VirtualModel 名の配列 (明示的に指定する必要がある)
   */
  enabledVirtualModelNames?: string[]
  /**
   * true を指定すると レコードコメント関連Modelは生成されるが、コメント機能は表示されなくなる。 (default false)
   */
  hidden?: boolean
  /**
   * true に設定すると、スレッドでまとめる機能を有効化する (デフォルトでは無効)
   */
  useThread?: boolean
  /**
   * true に設定すると、元レコード => レコードコメント用のリレーションシップを生成しない (default false)
   */
  disableAddOneToManyRelationship?: boolean
  /**
   * 元レコード => レコードコメント用のリレーションシップを貼る際のカラム名を変更したい場合に指定する, Default は `ext__record_comments`
   */
  recordCommentOneToManyColName?: string | typeof recordCommentOneToManyColName
  /**
   * 表示スタイルの指定
   */
  displayType?: keyof typeof recordCommentDisplayStyles
  /**
   * レコードコメント拡張テーブルの名称を変更したい場合に指定する
   * Default は 自動生成で `${modelName}__extension__comments` となる
   */
  extensionModelName?: string
  /**
   * スレッドごとのステータスを定義する
   * Default: {resolved: { label: '解決済み', variant: 'success' }, unresolved: {label: '未解決', variant: 'danger'}}
   */
  threadStates?: {
    [key: string]: {
      label: string
      variant?: StyleVariant
      default?: boolean
    }
  }
  /**
   * スレッド、コメント一覧の表示順(作成日の昇順 or 降順)を指定する
   * Default: 'asc'
   */
  createdAtOrder?: 'asc' | 'desc'
  /**
   * コメント保存成功時のメッセージを指定する
   * 接頭辞として、スレッド追加時は'スレッドを'、コメント追加時は'コメントを'が付与されるので、続きのメッセージを指定する
   * Default: storeMethodsのデフォルト => "保存しました"
   */
  successToastMessage?: string
  /**
   * コメント機能の拡張設定
   */
  features?: {
    /**
     * 複数ファイルアップロード機能
     * Default: { enabled: true }
     */
    multiFileUpload?: {
      enabled?: boolean
      maxFiles?: number // 最大ファイル数（デフォルト: 無制限）
    }
    /**
     * コメントの一時保存（ドラフト）機能
     * Default: { enabled: true, ttlDays: 3 }
     */
    draftSave?: {
      enabled?: boolean
      ttlDays?: number // TTL日数（デフォルト: 3日）
    }
    /**
     * コメント宛先のコピー機能（全員に通知ボタン）
     * Default: { enabled: true, buttonLabel: '全員に通知' }
     */
    copyReplies?: {
      enabled?: boolean
      buttonLabel?: string // ボタンのラベル（デフォルト: '全員に通知'）
    }
    /**
     * コメント毎の宛先コピー機能（各コメントに表示されるコピーボタン）
     * Default: { enabled: true, buttonLabel: '↩️ 宛先をコピー' }
     */
    copyCommentRecipients?: {
      enabled?: boolean
      buttonLabel?: string // ボタンのラベル（デフォルト: '↩️ 宛先をコピー'）
    }
    /**
     * コメントへのリアクション機能
     * Default: { enabled: true, types: [...デフォルトの4種類] }
     */
    reactions?: {
      enabled?: boolean
      types?: Array<{
        type: string
        emoji: string
        label: string
      }> // カスタムリアクションタイプ（デフォルト: good, thanks, thinking, check）
    }
  }
}

/**
 * 設定のデフォルト値
 */
export const getModelExtensionRecordCommentsConfigDefault =
  (): Partial<ModelExtensionRecordCommentsConfig> => {
    return {
      extensionType: 'recordComments',
      enabled: false,
      hidden: false,
      disableAddOneToManyRelationship: false,
      useThread: false,
      displayType: 'default',
      threadStates: {
        unresolved: { label: '未解決', variant: 'danger', default: true },
        resolved: { label: '解決済み', variant: 'success' },
      },
      createdAtOrder: 'asc',
      successToastMessage: null,
      features: {
        multiFileUpload: {
          enabled: true,
          maxFiles: undefined, // 無制限
        },
        draftSave: {
          enabled: true,
          ttlDays: 3,
        },
        copyReplies: {
          enabled: true,
          buttonLabel: '全員に通知',
        },
        copyCommentRecipients: {
          enabled: true,
          buttonLabel: '↩️ 宛先をコピー',
        },
        reactions: {
          enabled: true,
          types: [
            { type: 'good', emoji: '👍', label: 'いいね' },
            { type: 'thanks', emoji: '🙏', label: 'ありがとう' },
            { type: 'thinking', emoji: '🤔', label: '検討中' },
            { type: 'check', emoji: '✅', label: '確認済み' },
          ],
        },
      },
    }
  }

/**
 * レコードコメントを送信する際の送信先の種類
 * Serverside の ComposableFunctions の 設定に関連
 */
export const recordCommentSenderTargets = {
  replied: {
    label: 'リプライされたユーザ・組織',
  },
  threadRelated: {
    label: 'スレッドに関連するユーザ・組織',
  },
  // all: {
  //   label: '全ユーザ・組織',
  // },
}

export type UserAndOrgIds = {
  userIds: string[]
  orgCodes: string[]
}

export type ThreadRelatedUserAndOrgIds = {
  [threadId: string]: UserAndOrgIds
}

export const recordCommentCoreNotificationCategory = 'コメント'
